<template>
  <div class="account">
    <div class="card-header bg-default">
      Account Details
    </div>
    <div class="list-group list-group-flush">
      <div class="list-group-item">
        <strong>Member: </strong>
        <router-link :to="`/membership/members/${account.uuid}`">
          {{ account.dropdownLabel }} -
          <strong>{{ account.AccountType.name }}</strong>
        </router-link>
        <span class="badge float-right" :class="membershipStatus.label">
          {{ membershipStatus.text }}
        </span>
      </div>
      <div class="list-group-item" v-if="account.businessName">
        <strong>Business Name: </strong>{{ account.businessName }}
      </div>
      <div class="list-group-item">
        <strong>Smartcard: </strong>
        <span class="badge" :class="{
          'badge-danger': !account.cardId,
          'badge-success': account.cardId
        }">
          <span class="fa fa-id-card"></span>
          {{ !account.cardId ? 'No Smartcard Assigned' : account.Smartcard.cardNumber }}
        </span>
      </div>
      <div class="list-group-item" v-if="account.MemberDiscount">
        <strong>Member Discount:</strong>
        <span class="badge badge-primary">
          <strong>{{ account.MemberDiscount.percentDiscount }} %</strong>
        </span>
      </div>
      <div class="list-group-item">
        <strong>Balance: </strong>
        <span :class="{ 'text-danger' : account.credits < 0 }">
          {{ account.credits | to_dollar_amount_neg }}
        </span>
      </div>
      <div class="list-group-item">
        <strong>Registered Date:</strong>
        {{ account.registeredDate | short_date_format }}
      </div>
      <div class="list-group-item">
        <strong>Approval Date:</strong>
        {{ account.approvalDate | short_date_format }}
      </div>
      <div class="list-group-item">
        <strong>Address:</strong>
        {{ account.address }} {{ account.suburb }}
        {{ account.state }} {{ account.postcode }}
      </div>
      <div class="list-group-item">
        <strong>Date of Birth:</strong>
        {{ account.dateOfBirth | short_date_format }}
      </div>
      <div class="list-group-item" :class="{
          'list-group-item-danger': !account.CreditCards.length,
          'list-group-item-success': account.CreditCards.length
        }">
        <span class="fa fa-credit-card"></span>
        {{ !account.CreditCards.length ? 'No credit card on account.' : 'Has credit card on account.' }}
      </div>
      <div v-if="account.MasterUuid" class="list-group-item" :class="{
        'list-group-item-danger': !account.Master.CreditCards.length,
        'list-group-item-success': account.Master.CreditCards.length
      }">
        <span class="fa fa-credit-card"></span>
        {{ !account.Master.CreditCards.length ? 'Master account does not have a credit card on file.' : 'Master account has credit card on file.' }}
      </div>
    </div>
    <div class="card-body">
      <div class="card-title">Latest Reservation</div>
      <template v-if="account.Reservations[0]">
        <div class="list-group-item">
          <reservation-display :data="account.Reservations[0]">
          </reservation-display>
        </div>
      </template>
      <p class="list-group-item" v-else>
        No Reservations have been made for this account
      </p>
      <div class="row mt-3">
        <div class="col-md-6">
          <template v-if="account.isMasterAccount">
            <div class="card-title">Linked Drivers</div>
            <template v-for="driver in account.Drivers">
              <p>
                <router-link :to="{ path: '/membership/members/' + driver.uuid }">
                  {{ driver.dropdownLabel }}
                </router-link>
              </p>
            </template>
            <p>
              <strong>Allowed Drivers:</strong>
              {{ account.AccountType.maxAccounts }}
            </p>
          </template>
          <template v-if="!account.isMasterAccount && account.Master">
            <div class="card-title">Master Account</div>
            <p>
              <router-link :to="{ path: '/membership/members/' + account.Master.uuid }">
                {{ account.Master.dropdownLabel }}
              </router-link>
            </p>
          </template>
        </div>
        <div class="col-md-6">
          <div class="card-title">Latest Notes</div>
          <template v-if="account.AccountNotes[0]">
            <div class="list-group-item">
              <account-note-display :data="account.AccountNotes[0]">
              </account-note-display>
            </div>
          </template>
          <p class="list-group-item" v-else>
            No Notes have been made for this application
          </p>
        </div>
      </div>
    </div>
    <div class="card-footer text-right">
      <router-link class="btn btn-sm btn-amber" :to="{ path: `/membership/members/edit/${account.uuid}` }">
        <span class="fa fa-edit fa-fw"></span> Edit
      </router-link>
      <router-link class="btn btn-sm btn-primary" :to="{ path: `/membership/members/${account.uuid}` }">
        <span class="fa fa-search fa-fw"></span> View
      </router-link>
      <dropdown-input
        label="Account Actions"
        label-icon="fa-user"
        :is-small="true"
        :dropup="true"
        :actions="userActions(account, loggedInUser)"
        @action="executeAction($event, account)">
      </dropdown-input>
    </div>
  </div>
</template>

<script>
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay'
import ReservationDisplay from '@/components/displays/ReservationDisplay'
import DropdownInput from '@/components/inputs/DropdownInput'
import {
  ADD_ACCOUNT_NOTE,
  ADD_CREDITS,
  ADD_SMARTCARD,
  CHANGE_PASS_USER,
  CHANGE_PLAN_USER,
  EDIT_APPLICATION,
  EDIT_SMARTCARD,
  GENERATE_MONTHLY_INVOICE,
  INVITE_DRIVER,
  LOGIN_AS_USER,
  MAKE_ACTIVE,
  MAKE_INACTIVE,
  NEW_RESERVATION_USER,
  QUIT_MEMBER,
  REJOIN_MEMBER,
  RESTRICT_MEMBER,
  BLACKLIST_MEMBER,
  UN_BLACKLIST_MEMBER,
  UNASSIGN_SMARTCARD,
  UNRESTRICT_MEMBER
} from '@/vuex/types'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    AccountNoteDisplay,
    DropdownInput,
    ReservationDisplay
  },
  name: 'Account',
  props: {
    object: {
      type: Object
    },
    isFinanceAdmin: {
      type: Boolean,
      default () {
        return false
      }
    }
  },
  computed: {
    account () {
      return {
        AccountType: {},
        DriversLicense: {},
        DriversLicenseBack: {},
        Reservations: [],
        AccountNotes: [],
        CreditCards: [],
        ...this.object,
        ...this.updatedAccount
      }
    },
    membershipStatus () {
      let label = ''
      let text = ''
      let {
        activated,
        restricted,
        memberQuit
      } = this.account
      if (memberQuit) {
        label = 'badge-warning'
        text = 'Quit'
      } else if (restricted) {
        label = 'badge-danger'
        text = 'Restricted'
      } else if (!activated) {
        label = 'badge-default'
        text = 'Inactive'
      } else {
        label = 'badge-success'
        text = 'Active'
      }
      return { label, text }
    },
    ...mapGetters([
      'userActions',
      'loggedInUser'
    ])
  },
  data () {
    return {
      updatedAccount: {},
      url: 'https://techcar-dev.s3.amazonaws.com/'
    }
  },
  methods: {
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedAccount = result.updated
    },
    ...mapActions([
      ADD_ACCOUNT_NOTE,
      ADD_CREDITS,
      ADD_SMARTCARD,
      BLACKLIST_MEMBER,
      UN_BLACKLIST_MEMBER,
      CHANGE_PASS_USER,
      CHANGE_PLAN_USER,
      EDIT_APPLICATION,
      EDIT_SMARTCARD,
      GENERATE_MONTHLY_INVOICE,
      INVITE_DRIVER,
      LOGIN_AS_USER,
      MAKE_ACTIVE,
      MAKE_INACTIVE,
      NEW_RESERVATION_USER,
      QUIT_MEMBER,
      REJOIN_MEMBER,
      RESTRICT_MEMBER,
      UNASSIGN_SMARTCARD,
      UNRESTRICT_MEMBER
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/cards/Account.vue